import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCloseOnTabSwitch } from '@shared/hooks/useCloseOnTabSwitch';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Input, Skeleton, VerifiedBadge } from '@shared/ui';
import SuggestedHandleCard from './SuggestedHandleCard';
import { SignedImg } from '@shared/ui/SignedMedia';
import { SortHeader, THStyle, TDStyle, type SortDir } from './tableSort';

// Pending IG handles waiting to be linked to existing brands or promoted into
// new ones. Used to live as a tab inside /admin/bloggers but conceptually
// belongs under /admin/brands alongside existing-brand triage.

interface PendingBrandRow {
  handle: string;
  post_count: number;
  distinct_bloggers: number;
  sources_seen: string[];
  sample_blogger: string | null;
  classified_kind: 'brand' | 'blogger' | 'unknown' | null;
  classified_confidence: number | null;
  classified_manually: boolean | null;
  suggested_subcategory_id: string | null;
  subcategory_confidence: number | null;
  suggested_display_name: string | null;
  suggested_subcategory_label: string | null;
  existing_brand_match_id: string | null;
  match_confidence: number | null;
  existing_brand_name: string | null;
  from_ambassador?: boolean | null;
  ambassador_declared_by?: number | null;
  um_follower_count?: number | null;
  classify_country?: string | null;
  um_is_verified?: boolean | null;
}

// Columns the table can be sorted by (mapped to view columns for server-side .order()).
type SortKey = 'handle' | 'post_count' | 'distinct_bloggers' | 'um_follower_count' | 'classify_country';

interface BrandLookup {
  id: string;
  name: string;
  // Optional fields from find_brands_by_search RPC — present when the
  // brand matched via an alias rather than its canonical name. Lets the
  // dropdown render a "matched: <alias>" chip so admin understands why
  // a brand they didn't type appeared (e.g. typing "mbank" → "M Bank"
  // matched via alias).
  matchedVia?: string;   // 'name' | 'alias:name' | 'alias:alt_name' | 'alias:ig_handle'
  matchedText?: string;  // the actual alias string that matched
}

export default function PendingBrandHandles() {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  // search: controlled input in local state, debounced to URL
  const [search, setSearchLocal] = useState(params.get('ph_q') ?? '');
  const setSearch = (v: string) => {
    setSearchLocal(v);
  };
  const debouncedSearch = useDebounce(search, 300);
  // Separate, combinable searches: tagged @handle + bio keyword (AND with name search)
  const [tagged, setTagged] = useState(params.get('ph_tag') ?? '');
  const [bioKw, setBioKw] = useState(params.get('ph_bio') ?? '');
  const debouncedTagged = useDebounce(tagged, 300);
  const debouncedBio = useDebounce(bioKw, 300);
  // Sync debounced filters → URL. Only reset ph_page when the filters ACTUALLY
  // change — not on mount or unrelated re-renders (setParams identity isn't
  // stable, so an unconditional delete here was wiping ph_page on every page
  // click and snapping pagination back to page 1).
  const lastFilterSig = useRef<string | null>(null);
  useEffect(() => {
    const sig = JSON.stringify([debouncedSearch, debouncedTagged, debouncedBio]);
    const filtersChanged = lastFilterSig.current !== null && lastFilterSig.current !== sig;
    lastFilterSig.current = sig;
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (debouncedSearch) next.set('ph_q', debouncedSearch); else next.delete('ph_q');
      if (debouncedTagged) next.set('ph_tag', debouncedTagged); else next.delete('ph_tag');
      if (debouncedBio) next.set('ph_bio', debouncedBio); else next.delete('ph_bio');
      if (filtersChanged) next.delete('ph_page'); // reset to page 1 only on a real filter change
      return next;
    }, { replace: true });
  }, [debouncedSearch, debouncedTagged, debouncedBio, setParams]);
  const page = parseInt(params.get('ph_page') ?? '0', 10) || 0;
  const pageSize = parseInt(params.get('ph_ps') ?? '50', 10) || 50;
  const ambassadorOnly = params.get('ph_amb') === '1';
  const setPage = (p: number) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (p === 0) next.delete('ph_page'); else next.set('ph_page', String(p));
    return next;
  }, { replace: false });
  const setPageSize = (ps: number) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (ps === 50) next.delete('ph_ps'); else next.set('ph_ps', String(ps));
    next.delete('ph_page');
    return next;
  }, { replace: true });
  const setAmbassadorOnly = (v: boolean) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (v) next.set('ph_amb', '1'); else next.delete('ph_amb');
    next.delete('ph_page'); // reset page on filter change
    return next;
  }, { replace: false });
  const brandRecOnly = params.get('ph_brand') === '1';
  const setBrandRecOnly = (v: boolean) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (v) next.set('ph_brand', '1'); else next.delete('ph_brand');
    next.delete('ph_page'); // reset page on filter change
    return next;
  }, { replace: false });
  const country = params.get('ph_cty') ?? '';           // '' = all, '__NONE__' = unclassified
  const setCountry = (v: string) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (v) next.set('ph_cty', v); else next.delete('ph_cty');
    next.delete('ph_page'); // reset page on filter change
    return next;
  }, { replace: false });
  // Column sort (server-side). Empty sortKey ⇒ the default order (ambassador-declared-by
  // when the ambassador filter is on, otherwise post_count desc).
  const sortKey = (params.get('ph_sort') as SortKey | null) ?? null;
  const sortDir: SortDir = params.get('ph_dir') === 'asc' ? 'asc' : 'desc';
  const setSort = (key: string) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (sortKey === key) {
      // same column: toggle direction, and a third click clears back to default order
      if (sortDir === 'desc') { next.set('ph_sort', key); next.set('ph_dir', 'asc'); }
      else { next.delete('ph_sort'); next.delete('ph_dir'); }
    } else {
      next.set('ph_sort', key); next.set('ph_dir', 'desc'); // numeric cols read best high→low first
    }
    next.delete('ph_page');
    return next;
  }, { replace: false });
  const minFollowers = params.get('ph_fmin') ?? '';
  const maxFollowers = params.get('ph_fmax') ?? '';
  const minF = minFollowers.trim() === '' ? null : (parseInt(minFollowers, 10) || null);
  const maxF = maxFollowers.trim() === '' ? null : (parseInt(maxFollowers, 10) || null);
  const setFollower = (which: 'ph_fmin' | 'ph_fmax', v: string) => setParams(prev => {
    const next = new URLSearchParams(prev);
    const digits = v.replace(/\D/g, '');
    if (digits) next.set(which, digits); else next.delete(which);
    next.delete('ph_page'); // reset page on filter change
    return next;
  }, { replace: true });
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());
  const [linkTarget, setLinkTarget] = useState<PendingBrandRow | null>(null);
  const [cardHandle, setCardHandle] = useState<string | null>(null);
  const [brandPreview, setBrandPreview] = useState<{ id: string; name: string } | null>(null);

  const { data: queryResult, isLoading, error } = useSupabaseQuery<{ rows: PendingBrandRow[]; total: number }>(
    ['admin-pending-brand-handles', page, pageSize, debouncedSearch, debouncedTagged, debouncedBio, ambassadorOnly, brandRecOnly, country, minF, maxF, sortKey, sortDir],
    async () => {
      const from = page * pageSize;
      const to   = from + pageSize - 1;
      let q = supabase
        .from('v_admin_pending_brand_handles')
        .select('handle, post_count, distinct_bloggers, sources_seen, sample_blogger, classified_kind, classified_confidence, classified_manually, suggested_subcategory_id, subcategory_confidence, suggested_display_name, suggested_subcategory_label, existing_brand_match_id, match_confidence, existing_brand_name, um_full_name, um_follower_count, from_ambassador, ambassador_declared_by, classify_country, um_is_verified', { count: 'exact' });
      if (ambassadorOnly) q = q.eq('from_ambassador', true);
      // Ordering: an explicit column sort wins; otherwise fall back to the default order.
      if (sortKey) {
        q = q.order(sortKey, { ascending: sortDir === 'asc', nullsFirst: false }).order('handle', { ascending: true });
      } else if (ambassadorOnly) {
        q = q.order('ambassador_declared_by', { ascending: false }).order('handle', { ascending: true });
      } else {
        q = q.order('post_count', { ascending: false }).order('handle', { ascending: true });
      }
      // "AI suggested an existing brand" filter: rows where the model matched an existing brand_id
      if (brandRecOnly) q = q.not('existing_brand_match_id', 'is', null);
      // Country filter (classify_country); '__NONE__' = unclassified rows
      if (country === '__NONE__') q = q.is('classify_country', null);
      else if (country) q = q.eq('classify_country', country);
      // Followers range filter
      if (minF != null) q = q.gte('um_follower_count', minF);
      if (maxF != null) q = q.lte('um_follower_count', maxF);
      // Server-side search across handle + display name
      const term = debouncedSearch.trim();
      if (term.length > 0) {
        const safe = term.replace(/[,()*%]/g, '');
        q = q.or(`handle.ilike.%${safe}%,um_full_name.ilike.%${safe}%`);
      }
      // Combinable filters (AND): who they tagged + bio keyword
      const tag = debouncedTagged.trim().replace(/[,()*%]/g, '');
      if (tag.length > 0) q = q.ilike('um_mentioned_handles_text', `%${tag}%`);
      const bio = debouncedBio.trim().replace(/[,()*%]/g, '');
      if (bio.length > 0) q = q.ilike('um_bio', `%${bio}%`);
      const res = await q.range(from, to);
      if (res.error) return { data: null, error: res.error };
      return { data: { rows: (res.data ?? []) as PendingBrandRow[], total: res.count ?? 0 }, error: null };
    },
  );
  const data = queryResult?.rows;
  const total = queryResult?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Country facet for the dropdown ('__NONE__' = unclassified)
  const { data: countryList } = useSupabaseQuery<{ country: string; n: number }[]>(
    ['admin-pending-brand-countries'],
    async () => supabase.rpc('get_pending_brand_countries'),
  );

  // NOTE: the "AI Classify all" bulk button was removed (2026-06-26). It looped
  // the entire queue through the Haiku classify-handle Edge Function and a single
  // accidental click could burn the whole API budget (~$7 incident). Classification
  // is now done out-of-band; the admin only links/skips individual rows here.

  const onMoveToBlogger = async (handle: string) => {
    setBusy(handle);
    try {
      const { error: err } = await supabase
        .from('unknown_mentions')
        .upsert({
          handle,
          classify_kind: 'blogger',
          classify_confidence: 1.0,
          classify_reasoning: 'Manual override (admin moved from Brands to Suggested).',
          classify_ran_at: new Date().toISOString(),
          suggested_status: 'blogger',
        }, { onConflict: 'handle' });
      if (err) { alert(err.message); return; }
      setDone((prev) => new Set(prev).add(handle));
      qc.invalidateQueries({ queryKey: ['admin-pending-brand-handles'] });
      qc.invalidateQueries({ queryKey: ['admin-recommended-creators'] });
    } finally {
      setBusy(null);
    }
  };

  // Search is server-side; client only hides rows the admin just dealt with
  const filtered = (data ?? []).filter((r) => !done.has(r.handle));

  const onSkip = async (r: PendingBrandRow) => {
    setBusy(r.handle);
    try {
      const { error: err } = await supabase.rpc('admin_skip_handle_as_brand', {
        p_handle: r.handle,
        p_reason: null,
      });
      if (err) { alert(err.message); return; }
      setDone((prev) => new Set(prev).add(r.handle));
      qc.invalidateQueries({ queryKey: ['admin-pending-brand-handles'] });
    } catch (e) {
      alert((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <Input
          placeholder={t('providers.searchPlaceholder', { defaultValue: 'Search by name or @handle…' })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 200, flex: 1 }}
        />
        <Input
          placeholder={t('providers.suggested.taggedPlaceholder', { defaultValue: 'Tagged @handle (e.g. mbank)…' })}
          value={tagged}
          onChange={(e) => setTagged(e.target.value)}
          style={{ minWidth: 180, flex: 1, borderColor: tagged ? '#4a9eff' : undefined }}
        />
        <Input
          placeholder={t('providers.suggested.bioPlaceholder', { defaultValue: 'Bio keyword (e.g. реклама, UGC, блогер)…' })}
          value={bioKw}
          onChange={(e) => setBioKw(e.target.value)}
          style={{ minWidth: 180, flex: 1, borderColor: bioKw ? '#2ecc71' : undefined }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', flex: 1, minWidth: 240 }}>
          {t('providers.brands.help', {
            defaultValue: 'Handles seen as coauthor, usertag, or @mention on scraped posts but not yet linked to a brand. Link them to an existing brand or skip if it\'s a person.',
          })}
        </div>
      </div>

      {/* Filter: ambassador-declared brand candidates */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setAmbassadorOnly(!ambassadorOnly)}
          title="Brands that creators declare ambassadorship for in their bio but we don't have yet"
          style={{
            padding: '5px 10px', borderRadius: 6, fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)', cursor: 'pointer', fontFamily: 'inherit',
            background: ambassadorOnly ? 'rgba(155,93,229,0.22)' : 'transparent',
            color: ambassadorOnly ? '#b18cf0' : '#9aa',
            border: `1px solid ${ambassadorOnly ? '#9B5DE5' : 'rgba(255,255,255,0.15)'}`,
          }}
        >🎀 Ambassador-declared{ambassadorOnly ? ` · ${total}` : ''}</button>
        <button
          type="button"
          onClick={() => setBrandRecOnly(!brandRecOnly)}
          title="Handles where the AI matched an existing brand in our DB — review & confirm the link"
          style={{
            padding: '5px 10px', borderRadius: 6, fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)', cursor: 'pointer', fontFamily: 'inherit',
            background: brandRecOnly ? 'rgba(74,158,255,0.22)' : 'transparent',
            color: brandRecOnly ? '#4a9eff' : '#9aa',
            border: `1px solid ${brandRecOnly ? '#4a9eff' : 'rgba(255,255,255,0.15)'}`,
          }}
        >🔗 AI brand match{brandRecOnly ? ` · ${total}` : ''}</button>
        {/* Country filter (classify_country) */}
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          title="Filter by detected country"
          style={{
            padding: '5px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', fontFamily: 'inherit',
            background: 'transparent', color: country ? '#cde' : '#9aa',
            border: `1px solid ${country ? '#4a9eff' : 'rgba(255,255,255,0.15)'}`,
          }}
        >
          <option value="" style={{ background: '#1a1f2b' }}>
            {t('providers.brands.countryAll', { defaultValue: '🌍 All countries' })}
          </option>
          {(countryList ?? []).map((cc) => (
            <option key={cc.country} value={cc.country} style={{ background: '#1a1f2b' }}>
              {(cc.country === '__NONE__'
                ? t('providers.brands.countryNone', { defaultValue: 'Unclassified' })
                : cc.country)} · {cc.n.toLocaleString()}
            </option>
          ))}
        </select>
        {/* Followers range filter (um_follower_count) */}
        <span style={{ fontSize: 'var(--fs-small)', color: '#9aa', marginLeft: 4 }}>👥</span>
        <input
          type="text" inputMode="numeric"
          placeholder={t('providers.suggested.followersMin', { defaultValue: 'min' })}
          value={minFollowers}
          onChange={(e) => setFollower('ph_fmin', e.target.value)}
          style={{ width: 84, padding: '5px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', fontFamily: 'inherit',
            background: 'transparent', color: '#cde', border: '1px solid rgba(255,255,255,0.15)' }}
        />
        <span style={{ color: '#9aa' }}>–</span>
        <input
          type="text" inputMode="numeric"
          placeholder={t('providers.suggested.followersMax', { defaultValue: 'max' })}
          value={maxFollowers}
          onChange={(e) => setFollower('ph_fmax', e.target.value)}
          style={{ width: 84, padding: '5px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', fontFamily: 'inherit',
            background: 'transparent', color: '#cde', border: '1px solid rgba(255,255,255,0.15)' }}
        />
        {[1000, 5000, 10000].map((v) => (
          <button
            key={v} type="button"
            onClick={() => setFollower('ph_fmin', String(v))}
            style={{ padding: '4px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', cursor: 'pointer', fontFamily: 'inherit',
              background: minF === v ? 'rgba(74,158,255,0.2)' : 'transparent',
              color: minF === v ? '#4a9eff' : '#9aa',
              border: `1px solid ${minF === v ? '#4a9eff' : 'rgba(255,255,255,0.15)'}` }}
          >{v / 1000}k+</button>
        ))}
      </div>

      {/* Pagination header: total count + page controls */}
      <PaginationBar
        page={page} pageSize={pageSize} total={total} totalPages={totalPages}
        loaded={(data ?? []).length}
        onPrev={() => setPage(Math.max(0, page - 1))}
        onNext={() => setPage(Math.min(totalPages - 1, page + 1))}
        onPageSize={(n) => setPageSize(n)}
      />


      {isLoading && <Skeleton height={240} />}
      {error && <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>}
      {!isLoading && !error && filtered.length === 0 && (
        <div style={{ color: '#888', padding: 24, textAlign: 'center' }}>{t('providers.brands.empty', { defaultValue: 'No pending brand candidates.' })}</div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-small)', minWidth: 900 }}>
            <thead>
              <tr style={{ background: 'rgba(15,45,100,0.18)' }}>
                <SortHeader label={t('providers.brands.colHandle', { defaultValue: 'Handle' })}
                  col="handle" sortKey={sortKey} sortDir={sortDir} onSort={setSort} align="left" />
                <SortHeader label={t('providers.brands.colPosts', { defaultValue: 'Posts' })}
                  col="post_count" sortKey={sortKey} sortDir={sortDir} onSort={setSort} align="right" />
                <SortHeader label={t('providers.brands.colBloggers', { defaultValue: 'Bloggers' })}
                  col="distinct_bloggers" sortKey={sortKey} sortDir={sortDir} onSort={setSort} align="right" />
                <SortHeader label={t('providers.brands.colFollowers', { defaultValue: 'Followers' })}
                  col="um_follower_count" sortKey={sortKey} sortDir={sortDir} onSort={setSort} align="right" />
                <SortHeader label={t('providers.brands.colCountry', { defaultValue: 'Country' })}
                  col="classify_country" sortKey={sortKey} sortDir={sortDir} onSort={setSort} align="left" />
                <th style={{ ...THStyle, textAlign: 'left', cursor: 'default' }}>
                  {t('providers.brands.colSignals', { defaultValue: 'Signals' })}
                </th>
                <th style={{ ...THStyle, textAlign: 'right', cursor: 'default' }}>
                  {t('providers.brands.colActions', { defaultValue: 'Actions' })}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isBusy = busy === r.handle;
                return (
                  <tr key={r.handle} style={{ borderTop: '1px solid rgba(15,45,100,0.3)', opacity: isBusy ? 0.5 : 1 }}>
                    {/* Handle */}
                    <td style={{ ...TDStyle, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          type="button"
                          onClick={() => setCardHandle(r.handle)}
                          title={t('providers.suggested.openCard', { defaultValue: 'View card' })}
                          style={{
                            padding: 0, background: 'transparent', border: 'none',
                            color: '#bcd0ff', fontFamily: 'inherit', fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)',
                            cursor: 'pointer', textAlign: 'left',
                          }}
                        >@{r.handle}</button>
                        <VerifiedBadge verified={r.um_is_verified} />
                        <a
                          href={`https://www.instagram.com/${r.handle}/`}
                          target="_blank" rel="noopener noreferrer"
                          title={t('providers.openHandle', { defaultValue: 'Open on Instagram' })}
                          aria-label="Open on Instagram"
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 18, height: 18, borderRadius: 4, color: '#bcd0ff', opacity: 0.6,
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                          </svg>
                        </a>
                      </div>
                      {r.from_ambassador && (
                        <span
                          title={`Declared as a brand ambassador by ${r.ambassador_declared_by ?? 0} creator(s)' bio`}
                          style={{
                            display: 'inline-block', marginTop: 4,
                            padding: '1px 6px', borderRadius: 4, fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)',
                            background: 'rgba(155,93,229,0.15)', color: '#b18cf0', border: '1px solid rgba(155,93,229,0.45)',
                          }}
                        >🎀 {r.ambassador_declared_by ?? 0}</span>
                      )}
                    </td>
                    {/* Posts */}
                    <td style={{ ...TDStyle, textAlign: 'right', color: '#cde', fontVariantNumeric: 'tabular-nums' }}>
                      {r.post_count.toLocaleString()}
                    </td>
                    {/* Bloggers */}
                    <td style={{ ...TDStyle, textAlign: 'right', color: '#cde', fontVariantNumeric: 'tabular-nums' }}>
                      {r.distinct_bloggers.toLocaleString()}
                    </td>
                    {/* Followers */}
                    <td style={{ ...TDStyle, textAlign: 'right', color: r.um_follower_count != null ? '#bcd0ff' : '#556', fontVariantNumeric: 'tabular-nums' }}>
                      {r.um_follower_count != null ? r.um_follower_count.toLocaleString() : '—'}
                    </td>
                    {/* Country */}
                    <td style={{ ...TDStyle, color: r.classify_country ? '#cde' : '#556', whiteSpace: 'nowrap' }}>
                      {r.classify_country ?? '—'}
                    </td>
                    {/* Signals */}
                    <td style={{ ...TDStyle }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ color: '#778', fontSize: 'var(--fs-micro)' }}>{r.sources_seen.join(' / ')}</span>
                        {r.classified_kind && (
                          <span
                            title={r.classified_manually
                              ? 'Manually classified by admin'
                              : `AI confidence ${Math.round((r.classified_confidence ?? 0) * 100)}%`}
                            style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-semibold)',
                              background: r.classified_kind === 'brand' ? 'rgba(74,158,255,0.18)' : r.classified_kind === 'blogger' ? 'rgba(231,76,60,0.18)' : 'rgba(155,93,229,0.15)',
                              color: r.classified_kind === 'brand' ? '#4a9eff' : r.classified_kind === 'blogger' ? '#e74c3c' : '#9B5DE5',
                              border: '1px solid currentColor',
                            }}
                          >
                            {r.classified_manually ? '✓ ' : '🤖 '}{r.classified_kind}
                            {!r.classified_manually && r.classified_confidence != null && (
                              <> · {Math.round(r.classified_confidence * 100)}%</>
                            )}
                          </span>
                        )}
                        {r.suggested_subcategory_label && (
                          <span
                            title={`AI subcategory suggestion (${((r.subcategory_confidence ?? 0) * 100).toFixed(0)}%) — applied when you create a new brand`}
                            style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: 'var(--fs-micro)',
                              background: 'rgba(46,204,113,0.10)', color: '#2ecc71',
                              border: '1px solid rgba(46,204,113,0.4)',
                              maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                          >
                            🏷 {r.suggested_subcategory_label}
                          </span>
                        )}
                        {r.existing_brand_name && r.existing_brand_match_id && (
                          <button
                            type="button"
                            onClick={() => setBrandPreview({ id: r.existing_brand_match_id!, name: r.existing_brand_name! })}
                            title="Click to see what we already have for this existing brand"
                            style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-semibold)',
                              background: 'rgba(74,158,255,0.12)', color: '#4a9eff',
                              border: '1px solid rgba(74,158,255,0.5)', cursor: 'pointer', fontFamily: 'inherit',
                              maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                          >
                            🔗 → {r.existing_brand_name}
                            {r.match_confidence != null && <> · {Math.round(r.match_confidence * 100)}%</>}
                          </button>
                        )}
                      </div>
                    </td>
                    {/* Actions */}
                    <td style={{ ...TDStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => setLinkTarget(r)}
                          disabled={isBusy}
                          style={{
                            padding: '5px 10px', background: '#4a9eff', color: '#fff', border: 'none',
                            borderRadius: 6, cursor: isBusy ? 'wait' : 'pointer',
                            fontFamily: 'inherit', fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)',
                          }}
                        >
                          {t('providers.brands.linkAction', { defaultValue: 'Link to brand' })}
                        </button>
                        <button
                          type="button"
                          onClick={() => onMoveToBlogger(r.handle)}
                          disabled={isBusy}
                          title="This handle is actually a blogger — move to Suggested tab"
                          style={{
                            padding: '5px 8px', background: 'transparent', color: '#e74c3c',
                            border: '1px solid rgba(231,76,60,0.4)', borderRadius: 6,
                            cursor: isBusy ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-small)',
                          }}
                        >
                          → blogger
                        </button>
                        <button
                          type="button"
                          onClick={() => onSkip(r)}
                          disabled={isBusy}
                          title={t('providers.brands.skipTooltip', { defaultValue: 'Mark as not a brand' })}
                          style={{
                            padding: '5px 8px', background: 'transparent', color: '#9aa',
                            border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                            cursor: isBusy ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-small)',
                          }}
                        >
                          {isBusy ? '…' : t('providers.brands.skipAction', { defaultValue: 'Skip' })}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {cardHandle && (
        <SuggestedHandleCard handle={cardHandle} onClose={() => setCardHandle(null)} />
      )}

      {brandPreview && (
        <BrandPreviewModal brandId={brandPreview.id} brandName={brandPreview.name} onClose={() => setBrandPreview(null)} />
      )}

      {linkTarget && (
        <LinkHandleToBrandDialog
          handle={linkTarget.handle}
          suggestedDisplayName={linkTarget.suggested_display_name}
          suggestedSubcategoryId={linkTarget.suggested_subcategory_id}
          suggestedSubcategoryLabel={linkTarget.suggested_subcategory_label}
          classifiedKind={linkTarget.classified_kind}
          umFollowerCount={linkTarget.um_follower_count}
          existingBrandMatchId={linkTarget.existing_brand_match_id}
          existingBrandName={linkTarget.existing_brand_name}
          matchConfidence={linkTarget.match_confidence}
          onClose={() => setLinkTarget(null)}
          onLinked={() => {
            setDone((prev) => new Set(prev).add(linkTarget.handle));
            setLinkTarget(null);
            qc.invalidateQueries({ queryKey: ['admin-pending-brand-handles'] });
          }}
        />
      )}
    </>
  );
}

function LinkHandleToBrandDialog({
  handle, suggestedDisplayName, suggestedSubcategoryId, suggestedSubcategoryLabel,
  classifiedKind, umFollowerCount,
  existingBrandMatchId, existingBrandName, matchConfidence, onClose, onLinked,
}: {
  handle: string;
  suggestedDisplayName?: string | null;
  suggestedSubcategoryId?: string | null;
  suggestedSubcategoryLabel?: string | null;
  classifiedKind?: 'brand' | 'blogger' | 'unknown' | null;
  umFollowerCount?: number | null;
  existingBrandMatchId?: string | null;
  existingBrandName?: string | null;
  matchConfidence?: number | null;
  onClose: () => void;
  onLinked: () => void;
}) {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [newBrandName, setNewBrandName] = useState(suggestedDisplayName?.trim() || handle);

  useCloseOnTabSwitch(onClose, !busy);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(id);
  }, [search]);

  // Alias-aware brand search via RPC find_brands_by_search (Task #6 Part 0).
  // Searches brands.name AND brand_aliases.alias (types name/alt_name/ig_handle),
  // returns one row per matching brand with an optional `matched_via` chip so
  // admin understands why an unexpected brand surfaced (e.g. "M Bank" appearing
  // when searching "mbank" — matched via alias).
  const searchQ = useSupabaseQuery<BrandLookup[]>(
    ['admin-link-brand-search', debouncedSearch],
    async () => {
      const term = debouncedSearch.trim();
      if (term.length < 2) return { data: [], error: null };
      const { data, error } = await supabase.rpc('find_brands_by_search', {
        p_term: term, p_limit: 20,
      });
      if (error || !data) return { data: [], error };
      type Row = { brand_id: string; brand_name: string; matched_via: string; matched_text: string };
      const opts: BrandLookup[] = (data as Row[]).map((r) => ({
        id: r.brand_id,
        name: r.brand_name,
        matchedVia: r.matched_via,
        matchedText: r.matched_text,
      }));
      return { data: opts, error: null };
    },
  );

  // Post samples to help admin verify the AI match. Fetch only when there's a
  // suggestion to confirm — otherwise it's just clutter.
  type PostSample = {
    post_id: string; posted_at: string | null; caption: string | null;
    thumb_url: string | null; permalink: string | null;
    blogger_handle: string | null; source: string;
  };
  const postsQ = useSupabaseQuery<PostSample[]>(
    ['admin-handle-post-samples', handle],
    async () => supabase.rpc('get_handle_post_samples', { p_handle: handle, p_limit: 6 }),
  );

  // Subcategory override for create-brand mode. AI's pick is often wrong
  // (e.g. AluPlast → "Real Estate › Rental"). Admin can search and replace.
  type SubcatOption = { id: string; label: string };
  const [subcatOverride, setSubcatOverride] = useState<SubcatOption | null>(null);
  const [editingSubcat, setEditingSubcat] = useState(false);
  const [subcatSearch, setSubcatSearch] = useState('');
  const [debouncedSubcatSearch, setDebouncedSubcatSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSubcatSearch(subcatSearch), 200);
    return () => clearTimeout(id);
  }, [subcatSearch]);
  const subcatsQ = useSupabaseQuery<SubcatOption[]>(
    ['admin-link-subcat-search', debouncedSubcatSearch],
    async () => {
      const term = debouncedSubcatSearch.trim();
      if (term.length < 2) return { data: [], error: null };
      // Search across all three name columns — table has English name + name_ru + name_en
      // so admins can search in either language (e.g. "авто" or "auto" both work).
      const safe = term.replace(/[,()*%]/g, '');
      const { data, error } = await supabase
        .from('subcategories')
        .select('id, name, name_ru, name_en, categories!inner(name)')
        .eq('is_active', true)
        .or(`name.ilike.%${safe}%,name_ru.ilike.%${safe}%,name_en.ilike.%${safe}%`)
        .order('name', { ascending: true })
        .limit(15);
      if (error || !data) return { data: [], error };
      // Supabase typed-select returns join columns as arrays in strict TS mode,
      // even for to-one rels. Cast through unknown then narrow to flexible shape.
      type Joined = {
        id: string; name: string; name_ru: string | null; name_en: string | null;
        categories: { name: string } | { name: string }[];
      };
      const opts = (data as unknown as Joined[]).map((s) => {
        const cat = Array.isArray(s.categories) ? s.categories[0] : s.categories;
        // Show Russian name when present (most admin work is in Russian), otherwise fall back to canonical name.
        const display = s.name_ru?.trim() || s.name;
        return { id: s.id, label: `${cat?.name ?? '?'} › ${display}` };
      });
      return { data: opts, error: null };
    },
  );

  const effectiveSubcatId = subcatOverride?.id ?? suggestedSubcategoryId ?? null;
  const effectiveSubcatLabel = subcatOverride?.label ?? suggestedSubcategoryLabel ?? null;

  const onPick = async (brand: BrandLookup) => {
    setBusy(true);
    try {
      const { error: err } = await supabase.rpc('admin_link_handle_to_brand', {
        p_handle: handle,
        p_brand_id: brand.id,
      });
      if (err) { alert(err.message); return; }
      onLinked();
    } catch (e) {
      alert((e as Error)?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCreateBrand = async () => {
    const name = newBrandName.trim();
    if (!name) { alert('Brand name required'); return; }
    setBusy(true);
    try {
      const { error: err } = await supabase.rpc('promote_unknown_to_brand', {
        p_handle: handle,
        p_brand_name: name,
        p_subcategory_id: effectiveSubcatId,
      });
      if (err) { alert(err.message); return; }
      onLinked();
    } catch (e) {
      alert((e as Error)?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const candidates = searchQ.data ?? [];

  return (
    <div
      role="dialog" aria-modal="true" onClick={busy ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '10vh 16px', backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, background: 'var(--bg-elev, #161b22)',
          border: '1px solid rgba(15,45,100,0.5)', borderRadius: 12, padding: 20,
        }}
      >
        <h3 style={{ margin: '0 0 6px', fontSize: 'var(--fs-label)' }}>
          {t('providers.brands.linkTitle', { defaultValue: 'Link @{{handle}} to a brand', handle })}
        </h3>
        <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', marginBottom: 14 }}>
          {t('providers.brands.linkDesc', { defaultValue: 'Adds an ig_handle alias and re-resolves every post that referenced this handle.' })}
        </div>

        {/* The classifier already read this profile's bio and captions. When it says the
            account belongs to a PERSON, linking it to a company is almost always the
            generic-name trap: a shared word in the name mistaken for a shared identity.
            gorizont.312 — a musician whose bio reads "Album soon" — was linked to the
            residential complex ЖК «Горизонт» this way, and carried 10 posts and 4
            placements onto the wrong brand before anyone noticed. Manual links are the
            rarest path here and by far the least reliable: 2 errors in 61 rows, against
            0 in the 942 machine-made ones. So show what the machine already knows BEFORE
            the click, rather than hunting for the damage afterwards. */}
        {classifiedKind === 'blogger' && (
          <div style={{
            background: 'rgba(255,90,90,0.10)',
            border: '1px solid rgba(255,90,90,0.55)',
            borderRadius: 8, padding: 12, marginBottom: 12,
          }}>
            <div style={{ fontSize: 'var(--fs-caption)', color: '#ff9a9a', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              {t('providers.brands.linkBloggerWarnTitle', { defaultValue: 'Похоже на человека, а не на компанию' })}
            </div>
            <div style={{ fontSize: 'var(--fs-body)', color: '#f0dede' }}>
              {t('providers.brands.linkBloggerWarnBody', {
                defaultValue: 'Классификатор разметил @{{handle}} как блогера{{followers}}. Совпадение слова в названии — не доказательство: откройте профиль и убедитесь, что это тот же бизнес.',
                handle,
                followers: umFollowerCount ? ` (${umFollowerCount.toLocaleString('ru-RU')} подписчиков)` : '',
              })}
            </div>
            <a
              href={`https://www.instagram.com/${handle}/`} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', marginTop: 8, fontSize: 'var(--fs-small)', color: '#9ec3ff' }}
            >
              {t('providers.brands.linkOpenProfile', { defaultValue: 'Открыть профиль ↗' })}
            </a>
          </div>
        )}
        {!createMode && existingBrandMatchId && existingBrandName && (matchConfidence ?? 0) >= 0.6 && (
          <div style={{
            background: 'rgba(74,158,255,0.10)',
            border: '1px solid rgba(74,158,255,0.45)',
            borderRadius: 8, padding: 12, marginBottom: 12,
          }}>
            <div style={{ fontSize: 'var(--fs-caption)', color: '#9ec3ff', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              🤖 AI suggested match — {Math.round((matchConfidence ?? 0) * 100)}% confident
            </div>
            <div style={{ fontSize: 'var(--fs-body)', color: '#dde', marginBottom: 10 }}>
              Looks like the same brand as <strong>{existingBrandName}</strong>.
            </div>

            {/* Post preview so admin can verify the AI guess before confirming */}
            <div style={{ fontSize: 'var(--fs-caption)', color: '#778', marginBottom: 6 }}>
              {postsQ.isLoading
                ? 'Loading recent posts where @' + handle + ' appears…'
                : (postsQ.data ?? []).length === 0
                  ? `No recent posts found mentioning @${handle}.`
                  : `Recent posts mentioning @${handle} (${(postsQ.data ?? []).length}):`}
            </div>
            {(postsQ.data ?? []).length > 0 && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: 6, marginBottom: 10,
              }}>
                {(postsQ.data ?? []).map((p) => (
                  <a
                    key={p.post_id}
                    href={p.permalink ?? `https://www.instagram.com/p/${p.post_id}/`}
                    target="_blank" rel="noopener noreferrer"
                    title={`@${p.blogger_handle ?? '?'} · ${p.source}\n${(p.caption ?? '').slice(0, 200)}`}
                    style={{
                      position: 'relative', display: 'block', aspectRatio: '1 / 1',
                      borderRadius: 4, overflow: 'hidden',
                      border: '1px solid rgba(74,158,255,0.3)',
                      background: 'rgba(0,0,0,0.4)',
                      textDecoration: 'none',
                    }}
                  >
                    {p.thumb_url ? (
                      <SignedImg
                        src={p.thumb_url} alt=""
                        referrerPolicy="no-referrer"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 'var(--fs-micro)', color: '#778', textAlign: 'center', padding: 4,
                      }}>
                        no thumb
                      </div>
                    )}
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'rgba(0,0,0,0.65)', color: '#bcd0ff',
                      fontSize: 'var(--fs-micro)', padding: '2px 4px',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      @{p.blogger_handle ?? '?'} · {p.source}
                    </div>
                  </a>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => onPick({ id: existingBrandMatchId, name: existingBrandName })}
              disabled={busy}
              style={{
                width: '100%', padding: '8px 12px', background: '#4a9eff',
                color: '#fff', border: 'none', borderRadius: 6,
                cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)',
              }}
            >
              {busy ? '…' : `✓ Confirm: link to ${existingBrandName}`}
            </button>
          </div>
        )}
        {!createMode && (
          <>
            <Input
              autoFocus
              placeholder={t('providers.brands.linkSearch', { defaultValue: 'Search brand by name…' })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', marginBottom: 10 }}
            />
            {search.trim().length >= 2 && (
              <div style={{
                background: 'rgba(0,0,0,0.25)',
                border: '1px solid rgba(15,45,100,0.4)', borderRadius: 8,
                maxHeight: 240, overflowY: 'auto', marginBottom: 10,
              }}>
                {searchQ.isLoading || debouncedSearch !== search ? (
                  <div style={{ padding: 12, color: '#778' }}>…</div>
                ) : searchQ.error ? (
                  <div style={{ padding: 12, color: '#e74c3c', fontSize: 'var(--fs-small)' }}>
                    {String((searchQ.error as Error)?.message ?? searchQ.error)}
                  </div>
                ) : candidates.length === 0 ? (
                  <div style={{ padding: 12, color: '#778', fontSize: 'var(--fs-body)' }}>
                    {t('providers.brands.noBrandHits', { defaultValue: 'No brands match.' })}
                  </div>
                ) : candidates.map((b) => {
                  // Render a small chip when the brand matched via an alias
                  // rather than its canonical name — admin needs to know why
                  // a brand they didn't type for surfaced.
                  const viaAlias = b.matchedVia && b.matchedVia !== 'name';
                  const aliasKind = viaAlias ? b.matchedVia!.replace('alias:', '') : null;
                  return (
                    <button
                      key={b.id} type="button" onClick={() => onPick(b)} disabled={busy}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                        width: '100%', textAlign: 'left',
                        padding: '10px 12px', background: 'transparent', border: 'none',
                        color: '#dde', fontSize: 'var(--fs-body)', cursor: busy ? 'wait' : 'pointer',
                        fontFamily: 'inherit', borderBottom: '1px solid rgba(15,45,100,0.25)',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>{b.name}</span>
                      {viaAlias && (
                        <span
                          title={`Matched via ${aliasKind} alias: "${b.matchedText}"`}
                          style={{
                            fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-semibold)', padding: '2px 6px', borderRadius: 4,
                            background: 'rgba(155,93,229,0.10)', color: '#b18cf0',
                            border: '1px solid rgba(155,93,229,0.4)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          📎 via {aliasKind === 'ig_handle' ? 'IG' : 'alias'}: {b.matchedText}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button" onClick={() => setCreateMode(true)} disabled={busy}
              style={{
                width: '100%', padding: '10px 12px',
                background: 'transparent', color: '#9ec3ff',
                border: '1px dashed rgba(94,193,255,0.45)', borderRadius: 8,
                cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-body)',
              }}
            >
              + {t('providers.brands.createNew', { defaultValue: 'Create new brand' })} "{handle}"
            </button>
          </>
        )}
        {createMode && (
          <div style={{
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(94,193,255,0.45)', borderRadius: 8,
            padding: 12, marginBottom: 4,
          }}>
            <div style={{ fontSize: 'var(--fs-caption)', color: '#9aa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              {t('providers.brands.createNewLabel', { defaultValue: 'New brand name' })}
            </div>
            <Input
              autoFocus value={newBrandName}
              onChange={(e) => setNewBrandName(e.target.value)}
              placeholder={t('providers.brands.brandNamePlaceholder', { defaultValue: 'Brand name' })}
              style={{ width: '100%', marginBottom: 8 }}
            />
            {/* Subcategory: shows AI/override/none label with a Change toggle. */}
            <div style={{ marginBottom: 8 }}>
              {!editingSubcat ? (
                <div style={{
                  fontSize: 'var(--fs-caption)',
                  color: effectiveSubcatLabel ? '#2ecc71' : '#9aa',
                  padding: '6px 10px', borderRadius: 6,
                  background: effectiveSubcatLabel ? 'rgba(46,204,113,0.08)' : 'rgba(150,150,150,0.05)',
                  border: `1px solid ${effectiveSubcatLabel ? 'rgba(46,204,113,0.3)' : 'rgba(150,150,150,0.2)'}`,
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {effectiveSubcatLabel
                      ? <>{subcatOverride ? '✏️ Manual' : '🤖 AI suggested'} subcategory: <strong>{effectiveSubcatLabel}</strong></>
                      : 'No subcategory yet — assign in Taxonomy after creating, or pick one now.'}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setEditingSubcat(true); setSubcatSearch(''); }}
                    disabled={busy}
                    style={{
                      padding: '2px 8px', fontSize: 'var(--fs-micro)', borderRadius: 4,
                      background: 'transparent', color: '#9ec3ff',
                      border: '1px solid rgba(94,193,255,0.4)',
                      cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div style={{
                  background: 'rgba(0,0,0,0.25)',
                  border: '1px solid rgba(94,193,255,0.45)', borderRadius: 6,
                  padding: 8,
                }}>
                  <div style={{ fontSize: 'var(--fs-micro)', color: '#9aa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                    Pick subcategory
                  </div>
                  <Input
                    autoFocus
                    placeholder="Search by subcategory name…"
                    value={subcatSearch}
                    onChange={(e) => setSubcatSearch(e.target.value)}
                    style={{ width: '100%', marginBottom: 6 }}
                  />
                  {subcatSearch.trim().length >= 2 && (
                    <div style={{
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(15,45,100,0.4)', borderRadius: 6,
                      maxHeight: 180, overflowY: 'auto', marginBottom: 6,
                    }}>
                      {subcatsQ.isLoading || debouncedSubcatSearch !== subcatSearch ? (
                        <div style={{ padding: 8, color: '#778', fontSize: 'var(--fs-small)' }}>…</div>
                      ) : (subcatsQ.data ?? []).length === 0 ? (
                        <div style={{ padding: 8, color: '#778', fontSize: 'var(--fs-small)' }}>No subcategories match.</div>
                      ) : (subcatsQ.data ?? []).map((s) => (
                        <button
                          key={s.id} type="button"
                          onClick={() => { setSubcatOverride(s); setEditingSubcat(false); setSubcatSearch(''); }}
                          disabled={busy}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '6px 10px', background: 'transparent', border: 'none',
                            color: '#dde', fontSize: 'var(--fs-small)',
                            cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                            borderBottom: '1px solid rgba(15,45,100,0.25)',
                          }}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => { setSubcatOverride(null); setEditingSubcat(false); setSubcatSearch(''); }}
                      disabled={busy}
                      style={{
                        flex: 1, padding: '4px 8px', fontSize: 'var(--fs-caption)',
                        background: 'transparent', color: '#f5b700',
                        border: '1px solid rgba(245,183,0,0.4)', borderRadius: 4,
                        cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Clear (no subcategory)
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditingSubcat(false); setSubcatSearch(''); }}
                      disabled={busy}
                      style={{
                        padding: '4px 8px', fontSize: 'var(--fs-caption)',
                        background: 'transparent', color: '#9aa',
                        border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4,
                        cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div style={{ fontSize: 'var(--fs-caption)', color: '#778', marginBottom: 10 }}>
              {effectiveSubcatLabel
                ? `Creates the brand with the subcategory above, links @${handle} as ig_handle alias, and resolves any creators that have it in their bio.`
                : t('providers.brands.subcategoryLater', {
                    defaultValue: 'Subcategory can be assigned later in Taxonomy. Creates the brand, links @' + handle + ' as ig_handle alias, and resolves any creators that have it in their bio.',
                  })}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button" onClick={onCreateBrand} disabled={busy || !newBrandName.trim()}
                style={{
                  flex: 1, padding: '8px 12px',
                  background: '#4a9eff', color: '#fff', border: 'none', borderRadius: 6,
                  cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)',
                  opacity: busy || !newBrandName.trim() ? 0.5 : 1,
                }}
              >
                {busy ? '…' : t('providers.brands.createConfirm', { defaultValue: 'Create brand' })}
              </button>
              <button
                type="button" onClick={() => setCreateMode(false)} disabled={busy}
                style={{
                  padding: '8px 12px', background: 'transparent', color: '#9aa',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                  cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-body)',
                }}
              >
                {t('providers.brands.back', { defaultValue: 'Back' })}
              </button>
            </div>
          </div>
        )}
        <div style={{ marginTop: 14, textAlign: 'right' }}>
          <button
            type="button" onClick={onClose} disabled={busy}
            style={{
              padding: '6px 12px', background: 'transparent', color: '#9aa',
              border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
              cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-body)',
            }}
          >
            {t('providers.brands.cancel', { defaultValue: 'Cancel' })}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reusable pagination control. Used by both PendingBrandHandles and the
// Suggested-creators list in Providers.tsx — kept here so the latter can
// import it.
export function PaginationBar({
  page, pageSize, total, totalPages, loaded,
  onPrev, onNext, onPageSize,
}: {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  loaded: number;
  onPrev: () => void;
  onNext: () => void;
  onPageSize: (n: number) => void;
}) {
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to   = Math.min(total, page * pageSize + loaded);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 8, marginBottom: 8, flexWrap: 'wrap',
      padding: '6px 10px', borderRadius: 6,
      background: 'rgba(15,45,100,0.12)',
      border: '1px solid rgba(15,45,100,0.3)',
      fontSize: 'var(--fs-small)', color: '#bcd0ff',
    }}>
      <span>
        {total === 0
          ? 'No rows'
          : `Showing ${from}–${to} of ${total.toLocaleString()}`}
        {totalPages > 1 && ` · page ${page + 1}/${totalPages}`}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ color: '#9aa', fontSize: 'var(--fs-caption)' }}>
          per page&nbsp;
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
            style={{
              background: 'transparent', color: '#bcd0ff',
              border: '1px solid rgba(15,45,100,0.4)',
              borderRadius: 4, padding: '2px 4px', fontFamily: 'inherit',
            }}
          >
            {[50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button
          type="button" onClick={onPrev} disabled={page === 0}
          style={{
            padding: '4px 10px', background: 'transparent',
            color: page === 0 ? '#556' : '#9ec3ff',
            border: '1px solid rgba(94,193,255,0.4)', borderRadius: 4,
            cursor: page === 0 ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-caption)',
          }}
        >‹ Prev</button>
        <button
          type="button" onClick={onNext} disabled={page >= totalPages - 1}
          style={{
            padding: '4px 10px', background: 'transparent',
            color: page >= totalPages - 1 ? '#556' : '#9ec3ff',
            border: '1px solid rgba(94,193,255,0.4)', borderRadius: 4,
            cursor: page >= totalPages - 1 ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-caption)',
          }}
        >Next ›</button>
      </div>
    </div>
  );
}

/** Modal: existing data we already have for a recommended brand — its IG accounts
 *  (handle, role, followers, category, verified). Lets the admin judge whether the
 *  pending handle really belongs to this brand before confirming the link. */
function BrandPreviewModal({ brandId, brandName, onClose }: { brandId: string; brandName: string; onClose: () => void }) {
  const { data, isLoading } = useSupabaseQuery<{
    category: string | null;
    handles: Array<{ handle: string; role: string | null; followers_count: number | null; ig_category_name: string | null; is_verified: boolean | null; business_city: string | null }>;
  }>(
    ['brand-preview', brandId],
    async () => {
      const [bRes, igRes] = await Promise.all([
        supabase.from('brands').select('category').eq('id', brandId).maybeSingle(),
        supabase.from('brand_instagram')
          .select('handle, role, followers_count, ig_category_name, is_verified, business_city')
          .eq('brand_id', brandId)
          .order('followers_count', { ascending: false, nullsFirst: false }),
      ]);
      return {
        data: {
          category: (bRes.data as { category: string | null } | null)?.category ?? null,
          handles: (igRes.data ?? []) as never,
        },
        error: bRes.error ?? igRes.error ?? null,
      };
    },
  );
  const handles = data?.handles ?? [];
  return (
    <div
      role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#0d1730', border: '1px solid rgba(74,158,255,0.4)', borderRadius: 10, padding: 18, width: 'min(560px, 96vw)', maxHeight: '85vh', overflowY: 'auto', color: '#cdd9f5' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: 'var(--fs-h3)' }}>🔗 {brandName}</h3>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#9aa', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', marginBottom: 12 }}>
          Existing brand in DB{data?.category ? ` · ${data.category}` : ''}
        </div>
        {isLoading ? (
          <div style={{ color: '#778' }}>Loading…</div>
        ) : (
          <>
            <div style={{ fontSize: 'var(--fs-small)', color: '#bcd0ff', marginBottom: 6 }}>
              Existing IG accounts ({handles.length}):
            </div>
            {handles.length === 0 ? (
              <div style={{ color: '#778', fontStyle: 'italic', fontSize: 'var(--fs-small)' }}>
                No IG accounts on this brand yet — the pending handle would be its first.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {handles.map((h) => (
                  <div key={h.handle} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(15,45,100,0.5)', fontSize: 'var(--fs-small)' }}>
                    <a href={`https://www.instagram.com/${h.handle}/`} target="_blank" rel="noopener noreferrer" style={{ color: '#9ec3ff', fontWeight: 'var(--fw-semibold)', textDecoration: 'none' }}>
                      @{h.handle}
                    </a>
                    {h.is_verified && <span title="Verified">✔️</span>}
                    {h.role && <span style={{ color: '#778' }}>· {h.role}</span>}
                    <span style={{ marginLeft: 'auto', color: '#9aa', display: 'flex', gap: 10 }}>
                      {h.followers_count != null && <span>{h.followers_count.toLocaleString()} fl</span>}
                      {h.ig_category_name && <span style={{ color: '#778' }}>{h.ig_category_name}</span>}
                      {h.business_city && <span style={{ color: '#778' }}>📍{h.business_city}</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
